import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Search as SearchIcon, Calendar, MapPin, ArrowLeft, Camera, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImageUrl } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PhotoCard from "../components/PhotoCard";
import PhotoLightbox from "../components/PhotoLightbox";
import BundleBanner from "../components/BundleBanner";
import Seo from "../components/Seo";
import NotFoundView from "../components/NotFoundView";

export default function EventPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [bib, setBib] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [pageSize, setPageSize] = useState(120);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/events/by-slug/${slug}`);
        setEvent(r.data);
        // If the URL slug is an old/historical one, redirect to the canonical slug
        // so shared links keep working and we have one canonical URL for SEO.
        if (r.data.slug && r.data.slug !== slug) {
          navigate(`/event/${r.data.slug}`, { replace: true });
          return;
        }
        const photosRes = await api.get(`/photos/search?event_id=${r.data.id}&limit=5000`);
        setPhotos(photosRes.data);
      } catch (e) {
        setErr("Eventet hittades inte");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, navigate]);

  // Build SEO content from the loaded event (handled by <Seo> below).
  const seoTitle = event
    ? `${event.name} — Foton & bilder · Enduroskola`
    : "Event — Foton · Enduroskola";
  const seoDesc = event
    ? `Hitta dina foton från ${event.name} (${(event.date || "").slice(0, 10)}, ${event.location || "Sverige"}). ${event.photo_count ?? 0} bilder uppladdade. Sök på ditt startnummer.`
    : undefined;
  const seoImage = event ? (resolveImageUrl(event.cover_image_url) || undefined) : undefined;
  const seoJsonLd = event
    ? {
        "@context": "https://schema.org",
        "@type": "SportsEvent",
        "name": event.name,
        "startDate": event.date || undefined,
        "location": event.location
          ? { "@type": "Place", "name": event.location }
          : undefined,
        "url": `https://foton.enduroskola.se/event/${event.slug}`,
        "image": seoImage || undefined,
        "sport": "Enduro",
      }
    : null;

  const filtered = useMemo(() => {
    if (!bib) return photos;
    const needle = bib.trim();
    return photos.filter((p) => {
      const tags = p.tags_overridden
        ? (p.manual_numbers || [])
        : [...(p.detected_numbers || []), ...(p.manual_numbers || [])];
      return tags.includes(needle);
    });
  }, [photos, bib]);

  // If all photos share the same unit price, surface that as the bundle pitch price
  const eventUnitPrice = useMemo(() => {
    if (!photos.length) return null;
    const prices = new Set(photos.map((p) => p.price_sek));
    return prices.size === 1 ? photos[0].price_sek : null;
  }, [photos]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: document.title, url });
      } catch (_) {
        // user cancelled
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Länk kopierad!");
    }
  };

  if (err) return <NotFoundView message={err} />;

  if (loading || !event) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-3xl mx-auto p-12 text-white/50">Laddar...</div>
        <Footer />
      </div>
    );
  }

  const cover = resolveImageUrl(event.cover_image_url, "");

  return (
    <div className="App">
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={`/event/${event.slug}`}
        image={seoImage}
        jsonLd={seoJsonLd}
      />
      <Header />

      {/* Hero with cover background */}
      <section className="relative border-b border-white/5 overflow-hidden" data-testid="event-hero">
        {cover && (
          <div className="absolute inset-0">
            <img src={cover} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]/40" />
          </div>
        )}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <Link to="/" className="text-white/70 hover:text-brand text-sm mb-4 inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Alla event
          </Link>
          <p className="label-overline mb-3" data-testid="event-series">{event.series || "Event"}</p>
          <h1 className="font-display font-black uppercase text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-5 max-w-4xl" data-testid="event-name">
            {event.name}
          </h1>
          <div className="flex flex-wrap gap-5 text-white/80 text-sm mb-8">
            <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4 text-brand" /> {event.date}</span>
            <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-brand" /> {event.location}</span>
            <span className="inline-flex items-center gap-2"><Camera className="w-4 h-4 text-brand" /> {event.photo_count} foton</span>
          </div>

          {/* Bib search inline */}
          <div className="bg-black/60 backdrop-blur-md border border-white/10 p-3 max-w-xl flex gap-2" data-testid="event-bib-search">
            <SearchIcon className="w-5 h-5 text-brand self-center ml-2" />
            <input
              type="text"
              placeholder="Skriv ditt startnummer (t.ex. 143)"
              value={bib}
              onChange={(e) => setBib(e.target.value)}
              className="flex-1 bg-transparent text-white placeholder-white/40 focus:outline-none text-lg"
              data-testid="event-bib-input"
            />
            {bib && (
              <button
                onClick={() => setBib("")}
                className="text-white/50 hover:text-white text-sm px-2"
              >
                Rensa
              </button>
            )}
          </div>

          <div className="mt-4 flex gap-3">
            <button onClick={share} className="btn-secondary text-sm py-2 inline-flex items-center gap-2" data-testid="event-share">
              <Share2 className="w-4 h-4" /> Dela eventet
            </button>
            <button onClick={() => navigate(`/search?event_id=${event.id}`)} className="btn-secondary text-sm py-2">
              Avancerad sök
            </button>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-6">
          <p className="text-white/70">
            {bib
              ? <>Visar <span className="text-brand font-bold">{filtered.length}</span> träff{filtered.length === 1 ? "" : "ar"} för startnummer <span className="text-brand font-bold">#{bib}</span></>
              : <>Alla <span className="text-brand font-bold">{photos.length}</span> foton från {event.name}</>}
          </p>
        </div>
        {filtered.length === 0 ? (
          <div className="card-surface p-12 text-center">
            {bib ? (
              <>
                <p className="text-white mb-2 font-display uppercase text-xl">Inga träffar för #{bib}</p>
                <p className="text-white/60 text-sm">AI:n kan ha missat taggen — prova att scrolla igenom alla foton manuellt.</p>
                <button onClick={() => setBib("")} className="btn-secondary text-sm mt-4">Visa alla foton</button>
              </>
            ) : (
              <p className="text-white/60">Inga foton uppladdade ännu. Kolla tillbaka efter eventet!</p>
            )}
          </div>
        ) : (
          <>
            <BundleBanner unitPrice={eventUnitPrice} className="mb-4" />
            <p className="text-xs text-white/50 mb-3" data-testid="event-gallery-count">
              Visar <span className="text-white">{Math.min(filtered.length, pageSize)}</span> av <span className="text-white">{filtered.length}</span> foton
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="event-gallery">
              {filtered.slice(0, pageSize).map((p, i) => <PhotoCard key={p.id} photo={p} onClick={() => setLightboxIndex(i)} hidePhotographer />)}
            </div>
            {pageSize < filtered.length && (
              <div className="text-center mt-8">
                <button
                  onClick={() => setPageSize((s) => s + 120)}
                  className="btn-secondary px-8 py-3"
                  data-testid="event-load-more"
                >
                  Visa fler ({Math.min(120, filtered.length - pageSize)} till)
                </button>
                <button
                  onClick={() => setPageSize(filtered.length)}
                  className="text-brand hover:underline text-xs ml-3"
                  data-testid="event-load-all"
                >
                  eller visa alla {filtered.length}
                </button>
              </div>
            )}
          </>
        )}
      </section>
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={filtered}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
      <Footer />
    </div>
  );
}
